export interface ValidationIssue {
  field: string;
  message: string;
  type: 'error' | 'warning';
}

export const validateCertificate = (
  data: CertificateExtractedData,
  formType: string
): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  // Validate certifier type - CRITICAL requirement
  if (!data.certifier) {
    issues.push({
      field: 'certifier',
      message: 'You must select who the certifier is (Exporter, Importer, or Producer)',
      type: 'error',
    });
  }

  // Validate certifier details - required when certifier is selected
  if (data.certifier && !data.certifierDetails?.yourName) {
    issues.push({
      field: 'certifierDetails.yourName',
      message: `Please add ${data.certifier} details in the Certifier Details section`,
      type: 'error',
    });
  }

  // Check for duplicate information - warn if party details are filled when they shouldn't be
  if (data.certifier === 'Exporter' && data.exporterDetails?.companyName) {
    issues.push({
      field: 'exporterDetails.companyName',
      message: 'Exporter Details should be blank - use Certifier Details section instead',
      type: 'warning',
    });
  }

  if (data.certifier === 'Importer' && data.importerDetails?.companyName) {
    issues.push({
      field: 'importerDetails.companyName',
      message: 'Importer Details should be blank - use Certifier Details section instead',
      type: 'warning',
    });
  }

  if (data.certifier === 'Producer' && data.producerDetails?.companyName) {
    issues.push({
      field: 'producerDetails.companyName',
      message: 'Producer Details should be blank - use Certifier Details section instead',
      type: 'warning',
    });
  }

  // Warn if required party details are missing (when they're not the certifier)
  if (data.certifier !== 'Exporter' && !data.exporterDetails?.companyName) {
    issues.push({
      field: 'exporterDetails.companyName',
      message: 'Consider adding exporter information',
      type: 'warning',
    });
  }

  if (data.certifier !== 'Importer' && !data.importerDetails?.companyName) {
    issues.push({
      field: 'importerDetails.companyName',
      message: 'Consider adding importer information if known',
      type: 'warning',
    });
  }

  // Validate items
  if (!data.items || data.items.length === 0) {
    issues.push({
      field: 'items',
      message: 'At least one item is required',
      type: 'error',
    });
  } else {
    data.items.forEach((item, index) => {
      // Only validate required fields for INCLUDED items
      if (item.INCLUDED) {
        // Required item fields for included items only
        if (!item.description) {
          issues.push({
            field: `items.${index}.description`,
            message: `Item ${index + 1}: Description is required for included items`,
            type: 'error',
          });
        }
        if (!item.hts) {
          issues.push({
            field: `items.${index}.hts`,
            message: `Item ${index + 1}: HTS code is required for included items`,
            type: 'error',
          });
        }
        if (!item.origin) {
          issues.push({
            field: `items.${index}.origin`,
            message: `Item ${index + 1}: Origin country is required for included items`,
            type: 'error',
          });
        }
        if (!item.originCriterion) {
          issues.push({
            field: `items.${index}.originCriterion`,
            message: `Item ${index + 1}: Origin criterion is required for included items`,
            type: 'error',
          });
        }

        // Validate origin criterion format
        if (item.originCriterion && !isValidOriginCriterion(item.originCriterion)) {
          issues.push({
            field: `items.${index}.originCriterion`,
            message: `Item ${index + 1}: Invalid origin criterion format`,
            type: 'error',
          });
        }
      }

      // USMCA specific validation - still check all items for this warning
      if (formType === 'USMCA') {
        const origin = item.origin?.toUpperCase();
        if ((origin === 'CA' || origin === 'MX' || origin === 'US') && !item.INCLUDED) {
          issues.push({
            field: `items.${index}.INCLUDED`,
            message: `Item ${index + 1}: Should be included for USMCA (origin: ${origin})`,
            type: 'warning',
          });
        }
      }
    });

    // Check if at least one item is included
    const hasIncludedItems = data.items.some(item => item.INCLUDED);
    if (!hasIncludedItems) {
      issues.push({
        field: 'items',
        message: 'At least one item should be marked as included',
        type: 'warning',
      });
    }
  }

  return issues;
};

const isValidOriginCriterion = (criterion: string): boolean => {
  // Valid origin criterion codes for USMCA
  const validCodes = ['A', 'B', 'C', 'D', 'E'];
  const upperCriterion = criterion.toUpperCase();
  
  // Check for single letter codes
  if (validCodes.includes(upperCriterion)) {
    return true;
  }
  
  // Check for codes with numbers (e.g., B1, B2, etc.)
  if (/^[A-E]\d{1,2}$/.test(upperCriterion)) {
    return true;
  }
  
  return false;
};

export const getRequiredFieldsForSection = (_section: string): string[] => {
  // All party details are now optional
  return [];
};

export const formatFieldLabel = (field: string): string => {
  const labels: Record<string, string> = {
    'yourName': 'Your Name',
    'title': 'Title',
    'address': 'Address',
    'countryAndPostalCode': 'Country & Postal Code',
    'phone': 'Phone',
    'email': 'Email',
  };
  
  return labels[field] || field;
};